import { useCallback, useMemo } from 'react';
import {
  LocationScheduleRequestCount,
  ScheduleRequestCountResponse,
} from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { useQuery } from 'react-query';
import { ScheduleApi, ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAppScopeStore } from '@frontend/scope';
import { DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V2, DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V3 } from '../constants';
import { useScheduleBookingRequestsShallowStore } from '../stores/use-schedule-booking-requests-store';

const mergeRequestCounts = (
  v2Counts: LocationScheduleRequestCount[] = [],
  v3Counts: {
    locationId: string;
    total: number;
  }[] = []
) => {
  const locationMap = new Map<string, { locationId: string; total: number }>();

  // Merge V3 counts
  v3Counts.forEach(({ locationId = '', total = 0 }) => {
    locationMap.set(locationId, { locationId, total });
  });

  // Merge V2 counts
  v2Counts.forEach(({ locationId = '', total = 0 }) => {
    const existing = locationMap.get(locationId);
    if (existing) {
      existing.total += total;
    } else {
      locationMap.set(locationId, { locationId, total });
    }
  });

  const locationScheduleRequestCounts = Array.from(locationMap.values());
  const total = locationScheduleRequestCounts.reduce((sum, item) => sum + item.total, 0);

  return { total, locationScheduleRequestCounts };
};

type GetScheduleRequestAndAlertsCountType = {
  scheduleAlertsCount: number;
  isScheduleAlertsLoading: boolean;
  isAppointmentRequestCountLoading: boolean;
  scheduleRequestCount: ScheduleRequestCountResponse | undefined;
  refetchAlertCount: () => void;
  refetchAppointmentRequestCount: () => void;
};

export const useGetScheduleRequestAndAlertsCount = (): GetScheduleRequestAndAlertsCountType => {
  const { selectedLocationIds } = useAppScopeStore();
  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { combineBookingRequests } = useScheduleBookingRequestsShallowStore('combineBookingRequests');

  const {
    data: scheduleAlertsCount,
    isLoading: isScheduleAlertsLoading,
    refetch: refetchAlertCount,
  } = useQuery({
    queryKey: ['scheduleAlertsCount', selectedLocationIds],
    queryFn: async () => await ScheduleApi.getScheduleAlertsCount(selectedLocationIds),
  });

  const scheduleRequestCountQueryV2 = ScheduleQueries.useGetScheduleRequestCount(
    {
      locationIds: selectedLocationIds,
      statuses: DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V2,
    },
    combineBookingRequests || (!isLoadingFF && !isScheduleV3FlagEnabled)
  );

  const scheduleRequestCountQueryV3 = SchedulerV3.Queries.useListBookingSubmissionsQueries(
    selectedLocationIds.map((locationId) => ({
      request: {
        locationsIds: [locationId],
        reviewedStatuses: DEFAULT_SCHEDULE_REQUEST_STATUSES_PARAM_V3,
        limit: 1,
      },
      options: {
        enabled: !isLoadingFF && isScheduleV3FlagEnabled && !!locationId,
        select: (data) => ({ locationId, total: data.totalCount ? +data.totalCount : 0 }),
      },
    }))
  );

  const isAppointmentRequestCountLoading =
    scheduleRequestCountQueryV2.isLoading || scheduleRequestCountQueryV3.some((query) => query.isLoading);

  // For v3, refetch is handled by the query itself
  const refetchAppointmentRequestCount = () => {
    if (!isScheduleV3FlagEnabled || combineBookingRequests) {
      scheduleRequestCountQueryV2.refetch();
    }
  };

  const getV3Counts = useCallback(() => {
    const total = scheduleRequestCountQueryV3.reduce((acc, query) => acc + (query.data?.total ?? 0), 0);
    const locationScheduleRequestCounts = scheduleRequestCountQueryV3.map((query) => ({
      locationId: query.data?.locationId ?? '',
      total: query.data?.total ?? 0,
    }));

    return { total, locationScheduleRequestCounts };
  }, [scheduleRequestCountQueryV3]);

  const scheduleRequestCount = useMemo(() => {
    if (combineBookingRequests) {
      const v2Data = scheduleRequestCountQueryV2.data?.locationScheduleRequestCounts ?? [];
      const v3Data = scheduleRequestCountQueryV3.map((query) => ({
        locationId: query.data?.locationId ?? '',
        total: query.data?.total ?? 0,
      }));
      return mergeRequestCounts(v2Data, v3Data);
    }

    return isScheduleV3FlagEnabled ? getV3Counts() : scheduleRequestCountQueryV2.data;
  }, [
    combineBookingRequests,
    getV3Counts,
    isScheduleV3FlagEnabled,
    scheduleRequestCountQueryV2.data,
    scheduleRequestCountQueryV3,
  ]);

  return {
    scheduleAlertsCount: scheduleAlertsCount || 0,
    isScheduleAlertsLoading,
    refetchAlertCount,
    scheduleRequestCount,
    isAppointmentRequestCountLoading,
    refetchAppointmentRequestCount,
  };
};
